import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { IMAGES } from "@/lib/images";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2, ClipboardCheck } from "lucide-react";

const COUNTIES = ["Carlow","Cavan","Clare","Cork","Donegal","Dublin","Galway","Kerry","Kildare","Kilkenny","Laois","Leitrim","Limerick","Longford","Louth","Mayo","Meath","Monaghan","Offaly","Roscommon","Sligo","Tipperary","Waterford","Westmeath","Wexford","Wicklow"];

export default function BookAssessment() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", county: "", area_estimate: "", service_type: "", timeframe: "", message: "", company: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please enter your name and email."); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/enquiries", { ...form, type: "assessment", source_page: "book-assessment" });
      setDone(true);
      toast.success(res.data.message);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="relative bg-brand-navy text-white">
        <div className="absolute inset-0 opacity-25"><img src={IMAGES.hero} alt="" className="h-full w-full object-cover" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <span className="font-mono-spec text-xs uppercase tracking-widest text-white">Book a Site Assessment</span>
          <h1 className="mt-3 font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Request a site assessment</h1>
          <p className="mt-5 max-w-2xl text-lg text-white/80">Tell us about your grounds and we'll arrange a visit to advise on the right solution. Submitting this form sends a request — we'll confirm a time with you.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {done ? (
          <div className="rounded-3xl border border-brand-navy/10 bg-white p-10 text-center" data-testid="assessment-success">
            <CheckCircle2 className="mx-auto h-16 w-16 text-brand-navy" />
            <h2 className="mt-4 font-display font-bold text-2xl text-brand-ink">Request received</h2>
            <p className="mt-2 text-muted-foreground">Your enquiry has been received. Our team will contact you to discuss your requirements.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-3xl border border-brand-navy/10 bg-white p-6 md:p-10">
            <div className="flex items-center gap-3 text-brand-navy"><ClipboardCheck className="h-6 w-6" /><h2 className="font-display font-bold text-2xl">Assessment request</h2></div>
            <input type="text" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" tabIndex={-1} aria-hidden />
            <div className="mt-6 grid gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div><Label htmlFor="a-name">Name *</Label><Input id="a-name" data-testid="assessment-name" value={form.name} onChange={(e) => set("name", e.target.value)} required /></div>
                <div><Label htmlFor="a-email">Email *</Label><Input id="a-email" type="email" data-testid="assessment-email" value={form.email} onChange={(e) => set("email", e.target.value)} required /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div><Label htmlFor="a-phone">Phone</Label><Input id="a-phone" data-testid="assessment-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
                <div><Label>County</Label>
                  <Select value={form.county} onValueChange={(v) => set("county", v)}>
                    <SelectTrigger data-testid="assessment-county"><SelectValue placeholder="Select county" /></SelectTrigger>
                    <SelectContent>{COUNTIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div><Label htmlFor="a-area">Approx. lawn/site area (m²)</Label><Input id="a-area" data-testid="assessment-area" value={form.area_estimate} onChange={(e) => set("area_estimate", e.target.value)} placeholder="Unknown is fine" /></div>
                <div><Label>Interested in</Label>
                  <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                    <SelectTrigger data-testid="assessment-servicetype"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mowing">Mowing</SelectItem>
                      <SelectItem value="marking">Line marking</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Preferred timeframe</Label>
                <Select value={form.timeframe} onValueChange={(v) => set("timeframe", v)}>
                  <SelectTrigger data-testid="assessment-timeframe"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asap">As soon as possible</SelectItem>
                    <SelectItem value="1-3 months">1–3 months</SelectItem>
                    <SelectItem value="3-6 months">3–6 months</SelectItem>
                    <SelectItem value="exploring">Just exploring</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label htmlFor="a-msg">Anything else we should know?</Label><Textarea id="a-msg" data-testid="assessment-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Access, slopes, obstacles, existing setup…" /></div>
            </div>
            <Button type="submit" disabled={submitting} size="lg" className="mt-6 w-full bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold" data-testid="assessment-submit">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : "Request assessment"}
            </Button>
          </form>
        )}
      </section>
    </div>
  );
}
